import React, { useState } from "react";
import {
  FaStar,
  FaRegStar,
  FaStarHalfAlt,
  FaUser,
  FaThumbsUp,
  FaFlag,
} from "react-icons/fa";
import { MdVerified } from "react-icons/md";

const ProductReviews = ({
  reviews = [],
  averageRating = 0,
  reviewCount = 0,
}) => {
  const [expandedReviews, setExpandedReviews] = useState({});
  const [selectedFilter, setSelectedFilter] = useState("all");

  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating - fullStars >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <FaStar key={`full-${i}`} className="h-4 w-4 text-yellow-400" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <FaStarHalfAlt key="half" className="h-4 w-4 text-yellow-400" />
      );
    }

    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <FaRegStar key={`empty-${i}`} className="h-4 w-4 text-gray-300" />
      );
    }

    return stars;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : "";
    const last = lastName ? lastName.charAt(0).toUpperCase() : "";
    return first + last || "U";
  };

  const toggleReviewExpansion = (reviewId) => {
    setExpandedReviews((prev) => ({
      ...prev,
      [reviewId]: !prev[reviewId],
    }));
  };

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach((review) => {
      if (review.rating >= 1 && review.rating <= 5) {
        distribution[Math.floor(review.rating)]++;
      }
    });
    return distribution;
  };

  const filteredReviews =
    selectedFilter === "all"
      ? reviews
      : reviews.filter(
          (review) => Math.floor(review.rating) === parseInt(selectedFilter)
        );

  const ratingDistribution = getRatingDistribution();

  return (
    <div className="container mx-auto px-4 py-6 border-t border-gray-200 mt-4">
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Customer Reviews
          </h2>
          <p className="text-gray-600">
            See what our customers are saying about this product
          </p>
        </div>

        {/* Overall Rating Summary */}
        <div className="bg-gradient-to-r from-orange-50 to-red-50 p-8 rounded-2xl border border-orange-100 mb-8 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Rating Overview */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-3 mb-2">
                  <span className="text-5xl font-bold text-gray-900">
                    {Number(averageRating || 0).toFixed(1)}
                  </span>
                  <div className="flex items-center">
                    {renderRatingStars(averageRating || 0)}
                  </div>
                </div>
                <div className="text-gray-600">
                  <span className="font-semibold text-lg">{reviewCount}</span>{" "}
                  customer review{reviewCount !== 1 ? "s" : ""}
                </div>
              </div>

              {/* Rating Breakdown */}
              <div className="space-y-2 min-w-[280px]">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = ratingDistribution[rating] || 0;
                  const percentage =
                    reviewCount > 0 ? (count / reviewCount) * 100 : 0;

                  return (
                    <div key={rating} className="flex items-center gap-3">
                      <span className="text-sm font-medium w-6">{rating}</span>
                      <FaStar className="w-4 h-4 text-yellow-400" />
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-yellow-400 to-orange-400 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600 w-8">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Review Stats */}
            <div className="text-center lg:text-right">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <MdVerified className="text-green-500" />
                <span className="text-sm font-medium text-gray-700">
                  Verified Reviews
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Based on verified purchases
              </p>
            </div>
          </div>
        </div>

        {/* Filter Buttons */}
        {reviewCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setSelectedFilter("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedFilter === "all"
                  ? "bg-orange-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All Reviews ({reviewCount})
            </button>
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = ratingDistribution[rating] || 0;
              if (count === 0) return null;

              return (
                <button
                  key={rating}
                  onClick={() => setSelectedFilter(rating.toString())}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedFilter === rating.toString()
                      ? "bg-orange-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {rating} Star{rating !== 1 ? "s" : ""} ({count})
                </button>
              );
            })}
          </div>
        )}

        {/* Reviews List */}
        {filteredReviews && filteredReviews.length > 0 ? (
          <div className="space-y-6">
            {filteredReviews.map((review) => {
              const isExpanded = expandedReviews[review._id];
              const shouldTruncate =
                review.reviewText && review.reviewText.length > 200;

              return (
                <div
                  key={review._id}
                  className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  {/* Reviewer Info */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-red-100 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                      {review.user?.firstName || review.user?.lastName ? (
                        <span className="text-orange-600 font-bold text-lg">
                          {getInitials(
                            review.user.firstName,
                            review.user.lastName
                          )}
                        </span>
                      ) : (
                        <FaUser className="text-orange-600" size={20} />
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                        <div className="flex items-center gap-3">
                          <h4 className="font-semibold text-gray-900 text-lg">
                            {review.user?.firstName && review.user?.lastName
                              ? `${review.user.firstName} ${review.user.lastName}`
                              : "Anonymous User"}
                          </h4>
                          <MdVerified className="text-green-500" size={18} />
                        </div>
                        <span className="text-sm text-gray-500">
                          {formatDate(review.reviewedAt || review.createdAt)}
                        </span>
                      </div>

                      {/* Rating */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center">
                          {renderRatingStars(review.rating)}
                        </div>
                        <span className="text-sm text-gray-600 font-medium bg-gray-100 px-2 py-1 rounded">
                          {review.rating}/5
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Review Text */}
                  {review.reviewText && (
                    <div className="mb-6">
                      <div className="bg-gray-50 p-2 rounded-xl border-l-4 border-orange-300">
                        <p className="text-gray-700 leading-relaxed">
                          {shouldTruncate && !isExpanded
                            ? `${review.reviewText.substring(0, 200)}...`
                            : review.reviewText}
                        </p>
                        {shouldTruncate && (
                          <button
                            onClick={() => toggleReviewExpansion(review._id)}
                            className="text-orange-600 hover:text-orange-700 text-sm font-medium mt-2 transition-colors"
                          >
                            {isExpanded ? "Show less" : "Read more"}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Review Images */}
                  {review.images && review.images.length > 0 && (
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">
                        Customer Photos
                      </h5>
                      <div className="flex flex-wrap gap-3">
                        {review.images.map((image, index) => (
                          <div
                            key={index}
                            className="w-[50px] h-[50px] sm:w-[70px] sm:h-[70px] md:w-[80px] md:h-[80px] rounded-xl overflow-hidden border-2 border-gray-200 hover:border-orange-300 transition-colors"
                          >
                            <img
                              src={image}
                              alt={`Review image ${index + 1}`}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-200 cursor-pointer"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 bg-gradient-to-b from-gray-50 to-white rounded-2xl border-2 border-dashed border-gray-200">
            <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaStar className="text-orange-400" size={32} />
            </div>
            <h3 className="text-2xl font-semibold text-gray-700 mb-3">
              {selectedFilter === "all"
                ? "No Reviews Yet"
                : `No ${selectedFilter} Star Reviews`}
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {selectedFilter === "all"
                ? "Be the first to review this product and help others make informed decisions."
                : `Try selecting a different rating filter to see more reviews.`}
            </p>
            {selectedFilter !== "all" && (
              <button
                onClick={() => setSelectedFilter("all")}
                className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors"
              >
                Show All Reviews
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductReviews;
